import type { CompositePrimaryKey, Filter, FilterRelationalQuantifier as FilterRelationalQuantifierType, GlobalSingleRelationFieldId, ResolvedMonospacePrimitiveField } from '#layers/data-model/public';
import type { FilterSchemaCollection, FilterSchemaField, FilterSchemaRelationField } from '#layers/filters/components/Filter/utils/filter-schema';
import type { FilterSchemaContext } from '#layers/filters/components/Filter/utils/filter-schema-context';
import type { RelatedItemOperator, RelatedItemTerminalMatch } from '#layers/filters/components/Filter/utils/related-item-match';
import type { RelatedItemTarget } from '#layers/filters/components/Filter/utils/related-item-target';
import { isNonArrayRecord } from '#layers/base/public';
import { FilterOperator, FilterRelationalQuantifier, isM2M, useWorkspaceSchemaStore } from '#layers/data-model/public';
import { injectFilterSchemaContext } from '#layers/filters/components/Filter/utils/filter-schema-context';
import { negateOperator, parseTerminalMatch } from '#layers/filters/components/Filter/utils/related-item-match';
import { relatedItemKeyFields, relatedItemMatchColumns, RelatedItemTargetKinds, resolveRelatedItemTarget, serializeRelatedItemMatch, toTraversalTarget, traversedColumns, traversesToMany } from '#layers/filters/components/Filter/utils/related-item-target';

// `_every` (a subset match) is intentionally excluded: it reads as a footgun in an identity picker, so
// an `_every` filter falls through to the generic field editor rather than rendering as a related-item pill.
const TO_MANY_QUANTIFIERS = new Set<string>([
	FilterRelationalQuantifier.Some,
	FilterRelationalQuantifier.None,
]);

export interface RelatedItemClause {
	target: RelatedItemTarget;
	/** The fields of `target.targetCollection` that each key carries. */
	keyFields: ResolvedMonospacePrimitiveField[];
	operator: RelatedItemOperator;
	keys: CompositePrimaryKey[];
	traversesToMany: boolean;
	displayFieldPath: GlobalSingleRelationFieldId[];
	displayFieldPathRealIndices: number[];
}

/** What a related-item clause serializes from. */
export interface RelatedItemMatch {
	target: RelatedItemTarget;
	operator: RelatedItemOperator;
	keys: CompositePrimaryKey[];
}

function isRelationField(field: FilterSchemaField): field is FilterSchemaRelationField {
	return !('type' in field);
}

function isPositiveMembership(operator: RelatedItemOperator): boolean {
	return operator === FilterOperator.EQ || operator === FilterOperator.IN;
}

function serializeRelatedItemClause({ target, operator, keys }: RelatedItemMatch): Filter {
	return serializeRelatedItemMatch(target, operator, keys);
}

/** Pass `schemaContext` when the same component provides it, since a component cannot inject what it provides. */
export function useRelatedItemMatch(schemaContext?: FilterSchemaContext) {
	const schema = schemaContext ?? injectFilterSchemaContext();
	const schemaStore = useWorkspaceSchemaStore();

	// The real indices of the hops kept in the display path
	function displayHopRealIndices(path: GlobalSingleRelationFieldId[]): number[] {
		return path.reduce<number[]>((indices, _id, index) => {
			const previous = index === 0 ? undefined : schemaStore.findSingleRelationFieldById(path[index - 1]!);
			if (index === 0 || !(previous && isM2M(previous))) indices.push(index);
			return indices;
		}, []);
	}

	/**
	 * A terminal written on the foreign key of a to-one relation of `collection`: the relation whose
	 * columns the terminal matches, with at least one key.
	 */
	function parseForeignKeyTerminal(value: unknown, collection: FilterSchemaCollection, path: GlobalSingleRelationFieldId[]) {
		for (const relation of collection.relationFields ?? []) {
			if (relation.isList) continue;

			const target = resolveRelatedItemTarget([...path, relation.id], schemaStore);
			if (target?.kind !== RelatedItemTargetKinds.ForeignKey) continue;

			const terminal = parseTerminalMatch(value, relatedItemMatchColumns(target));
			if (terminal && terminal.keys.length > 0) return { target, terminal };
		}
	}

	/**
	 * A terminal nested under the last hop. A foreign key target also accepts the traversal shape
	 * saved before matches were written on the foreign key (`{ author: { id: { _eq } } }`); editing it
	 * rewrites the clause on the foreign key. A saved traversal that compares the target's primary key
	 * rather than the referenced fields stays a traversal.
	 */
	function parseTraversedTerminal(value: unknown, path: GlobalSingleRelationFieldId[]): { target: RelatedItemTarget; terminal: RelatedItemTerminalMatch } | null {
		const target = resolveRelatedItemTarget(path, schemaStore);
		if (!target) return null;

		// A cleared selection holds an empty match under the relation (`{ relation: {} }`).
		if (isNonArrayRecord(value) && Object.keys(value).length === 0) return { target, terminal: { operator: FilterOperator.EQ, keys: [] } };

		const terminal = parseTerminalMatch(value, traversedColumns(relatedItemKeyFields(target)));
		if (terminal) return { target, terminal };
		if (target.kind !== RelatedItemTargetKinds.ForeignKey) return null;

		const traversal = toTraversalTarget(target);
		const primaryKeyTerminal = traversal && parseTerminalMatch(value, relatedItemMatchColumns(traversal));
		return primaryKeyTerminal ? { target: traversal, terminal: primaryKeyTerminal } : null;
	}

	function parseRelatedItemClause(filter: Filter, collection: FilterSchemaCollection): RelatedItemClause | null {
		const relationFieldPath: GlobalSingleRelationFieldId[] = [];
		let currentCollection: FilterSchemaCollection = collection;
		let currentObj: unknown = filter;
		let deepestQuantifier: FilterRelationalQuantifierType | undefined;

		while (isNonArrayRecord(currentObj) && Object.keys(currentObj).length === 1) {
			const apiName = Object.keys(currentObj)[0]!;
			const field = schema.findFieldByApiName(currentCollection, apiName);

			// A primitive first key (or a logical `_or`/`_not`) is the terminal match, not a relation hop.
			if (!field || !isRelationField(field)) break;

			let inner: unknown = currentObj[apiName];

			if (field.isList) {
				// A to-many hop wraps its inner match in a single relational quantifier (`_some`/`_none`);
				// polarity lives here, so the terminal below stays positive.
				if (!isNonArrayRecord(inner) || Object.keys(inner).length !== 1) break;

				const quantifier = Object.keys(inner)[0]!;
				if (!TO_MANY_QUANTIFIERS.has(quantifier)) break;

				if (deepestQuantifier && deepestQuantifier !== FilterRelationalQuantifier.Some) return null;

				deepestQuantifier = quantifier as FilterRelationalQuantifierType;
				inner = inner[quantifier];
			}

			const next = schema.findOppositeCollectionByRelationFieldId(field.id);
			if (!next) return null;

			relationFieldPath.push(field.id);
			currentCollection = next;
			currentObj = inner;
		}

		const parsed = parseForeignKeyTerminal(currentObj, currentCollection, relationFieldPath)
			?? (relationFieldPath.length > 0 ? parseTraversedTerminal(currentObj, relationFieldPath) : null);
		if (!parsed) return null;

		const { target, terminal } = parsed;
		const toMany = traversesToMany(target);
		const isExclusion = deepestQuantifier === FilterRelationalQuantifier.None;
		const displayFieldPathRealIndices = displayHopRealIndices(target.relationFieldPath);

		const clause = {
			target,
			keyFields: relatedItemKeyFields(target),
			keys: terminal.keys,
			traversesToMany: toMany,
			displayFieldPath: displayFieldPathRealIndices.map((index) => target.relationFieldPath[index]!),
			displayFieldPathRealIndices,
		};

		// A to-many match carries its polarity on the quantifier around a positive terminal.
		if (!toMany) return { ...clause, operator: terminal.operator };
		if (terminal.keys.length === 0) return { ...clause, operator: isExclusion ? FilterOperator.NIN : FilterOperator.IN };
		if (!isPositiveMembership(terminal.operator)) return null;

		return { ...clause, operator: isExclusion ? negateOperator(terminal.operator) : terminal.operator };
	}

	function isRelatedItemMatch(filter: Filter, collection: FilterSchemaCollection): boolean {
		return parseRelatedItemClause(filter, collection) !== null;
	}

	return { parseRelatedItemClause, serializeRelatedItemClause, isRelatedItemMatch };
}
